import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export const useSubscription = () => {
  const { user } = useAuth()
  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Récupérer le statut d'abonnement de l'utilisateur
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('user_infos')
        .select('*')
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = pas de résultat trouvé
        throw error
      }

      // Si pas de données, créer un objet par défaut avec le statut d'essai
      if (!data) {
        const userCreatedAt = new Date(user.created_at)
        const trialEndDate = new Date(userCreatedAt.getTime() + (30 * 24 * 60 * 60 * 1000))
        const now = new Date()
        const daysRemaining = Math.max(0, Math.ceil((trialEndDate - now) / (24 * 60 * 60 * 1000)))

        setSubscriptionStatus({
          user_id: user.id,
          email: user.email,
          user_created_at: user.created_at,
          subscription_id: null,
          subscription_type: null,
          subscription_start: null,
          subscription_end: null,
          is_active: false,
          amount_paid: null,
          trial_end_date: trialEndDate.toISOString(),
          user_status: daysRemaining > 0 ? 'trial' : 'expired',
          trial_days_remaining: daysRemaining,
          subscription_days_remaining: 0
        })
      } else {
        setSubscriptionStatus(data)
      }

      setError(null)
    } catch (err) {
      console.error('Erreur lors de la récupération du statut d\'abonnement:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Créer un nouvel abonnement - utilise une fonction RPC pour la sécurité
  const createSubscription = useCallback(async (subscriptionType, paymentData = {}) => {
    if (!user) throw new Error('Utilisateur non connecté')

    try {
      const startDate = new Date()
      const endDate = new Date()
      
      // Calculer la date de fin selon le type d'abonnement
      if (subscriptionType === 'monthly') {
        endDate.setMonth(endDate.getMonth() + 1)
      } else if (subscriptionType === 'yearly') {
        endDate.setFullYear(endDate.getFullYear() + 1)
      }

      const subscriptionData = {
        param_subscription_type: subscriptionType,
        param_start_date: startDate.toISOString(),
        param_end_date: endDate.toISOString(),
        param_is_active: true,
        ...paymentData
      }

      // Utiliser une fonction RPC pour créer l'abonnement de manière sécurisée
      const { data, error } = await supabase
        .rpc('create_user_subscription', subscriptionData)

      if (error) throw error

      // Rafraîchir le statut
      await fetchSubscriptionStatus()
      
      return data
    } catch (err) {
      console.error('Erreur lors de la création de l\'abonnement:', err)
      throw err
    }
  }, [user, fetchSubscriptionStatus])

  // Annuler un abonnement - utilise une fonction RPC pour la sécurité
  const cancelSubscription = useCallback(async (subscriptionId) => {
    try {
      const { error } = await supabase
        .rpc('cancel_user_subscription', { subscription_id: subscriptionId })

      if (error) throw error

      // Rafraîchir le statut
      await fetchSubscriptionStatus()
    } catch (err) {
      console.error('Erreur lors de l\'annulation de l\'abonnement:', err)
      throw err
    }
  }, [user, fetchSubscriptionStatus])

  // Vérifier si l'utilisateur a accès aux fonctionnalités premium
  // MODIFIÉ : Accès gratuit pour tous, pas besoin d'abonnement
  const hasAccess = useMemo(() => {
    return true // Toutes les fonctionnalités sont gratuites
  }, [subscriptionStatus])

  // Obtenir le message de statut à afficher
  const statusMessage = useMemo(() => {
    if (!subscriptionStatus) return ''

    switch (subscriptionStatus.user_status) {
      case 'trial':
        return `Essai gratuit - ${subscriptionStatus.trial_days_remaining} jour(s) restant(s)`
      case 'subscribed':
        return `Abonnement ${subscriptionStatus.subscription_type === 'monthly' ? 'mensuel' : 'annuel'} actif`
      case 'expired':
        return 'Abonnement expiré'
      default:
        return ''
    }
  }, [subscriptionStatus])

  useEffect(() => {
    fetchSubscriptionStatus()
  }, [fetchSubscriptionStatus])

  return {
    subscriptionStatus,
    loading,
    error,
    hasAccess,
    statusMessage,
    createSubscription,
    cancelSubscription,
    refreshStatus: fetchSubscriptionStatus
  }
} 